import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  MenuItem,
  Stack,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { EntityStatus } from "../types";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";

interface AddLinkProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddLink: React.FC<AddLinkProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "links"));

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [_tenantIdState, setTenantId] = useState(tenantId || "");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAdd = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createLink({
        name,
        entityClass: url.trim() || undefined,
        description: description.trim() || undefined,
        tenantId,
        status,
      });
      setSuccessOpen(true);
      setTimeout(() => {
        goBack();
      }, 800);
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to create link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    goBack();
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add Link"
        pageDescription="Create a new Link entity."
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: 600,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={3}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              placeholder="https://example.com"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Tenant ID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              fullWidth
            >
              <MenuItem value={tenantId || ''}>{tenantId}</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EntityStatus)}
              fullWidth
            >
              {Object.values(EntityStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Creating..." : "Add Link"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Link created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddLink;
